import { sortSubsessions } from '../utils/sort-subsessions.js';
// Transform session data for the modal from agenda data structure
const getSessionDataForModal = (session, speakersById = {}) => {
  if (!session) return null;

  return {
    // SB-9413: custom field values ride on the session by internal_name.
    ...session,
    id: session.id,
    title: session.title,
    description: session.description,
    date: session.range || `${session.starts_at} - ${session.ends_at}`,
    location: session.location,
    track: session.track,
    format: session.format,
    clientId: session.id,
    endTimeAddEvent: session.end_time_addEvent || session.end_time,
    endsAtAddEvent: session.ends_at_addEvent,
    startTimeAddEvent: session.start_time_addEvent || session.start_time,
    startsAtAddEvent: session.starts_at_addEvent,
    timezone: session.timezone,
    tags: session.tags?.map(tag => tag.name).join(', ') || '',
    tagsList: session.tags?.map(tag => tag.name) || [],
    level: session.level,
    language: session.language,
    // SB-10242 audit: SessionModal prints the credits line from `ceuCredits`;
    // the Agenda and Rooms pop-ups never set it, so they had no credits line.
    ceuCredits: session.ceu_credits,
    speakers:
      session.speakers?.map(speaker => ({
        id: speaker.id,
        name: speaker.full_name,
        salutation: speakersById[speaker.id]?.salutation || speaker.salutation || '',
        honorific: speakersById[speaker.id]?.honorific || speaker.honorific || '',
        title: speakersById[speaker.id]?.title || '',
        company: speakersById[speaker.id]?.company_name || '',
        photo: speakersById[speaker.id]?.photo || '',
        // SB-9832: present only when the org shows current disclosures on embeds.
        disclosure: speakersById[speaker.id]?.disclosure || speaker.disclosure || null,
        role: speaker.role || 'speaker',
        // SB-8853: slug-grouped roles with organizer labels for custom roles.
        role_slug: speaker.role_slug || speaker.role || 'speaker',
        role_label: speaker.role_label || null,
        role_label_plural: speaker.role_label_plural || null,
      })) || [],
    // SB-9083: admin order everywhere subsessions are listed, not just in the modal.
    subsessions: sortSubsessions(session.subsessions || []),
  };
};

const getSpeakerDataForModal = (speakerId, speakersById = {}, allSessions = []) => {
  const speaker = speakersById[speakerId];
  if (!speaker) return null;

  // Get sessions for this speaker from all sessions across all days/locations
  const speakerSessions = allSessions.filter(session => session.speakers?.some(s => s.id === speakerId));

  return {
    ...speaker,
    sessions: speakerSessions,
  };
};

/**
 * Convert time string (HH:MM) to minutes since midnight
 * @param {string} timeStr - Time in HH:MM format
 * @returns {number} Minutes since midnight
 */
const timeToMinutes = timeStr => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Helper function to process a group of sessions with positioning
 * @param {Array} group - Array of sessions in the group
 * @returns {Array} Sessions with positioning info
 */
const processGroup = group => {
  return group.map((session, index) => ({
    ...session,
    overlapIndex: index,
    overlapTotal: group.length,
    width: 100 / group.length, // Width as percentage
    leftOffset: (100 / group.length) * index, // Left offset as percentage
  }));
};

/**
 * Groups overlapping sessions and calculates their positioning
 * Sessions overlap if they start at the same time or if they share the first hour period
 * Optimized O(n log n) implementation using sweep line algorithm
 * @param {Array} sessions - Array of sessions for a location
 * @returns {Array} Array of sessions with positioning info
 */
const groupOverlappingSessions = sessions => {
  if (!sessions || sessions.length === 0) return [];

  // Sort sessions by start time - O(n log n)
  const sortedSessions = [...sessions].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

  const result = [];
  let currentGroup = [];
  let groupStartTime = null;

  // Single pass through sorted sessions - O(n)
  for (const session of sortedSessions) {
    const startTime = timeToMinutes(session.start_time);

    // If this is the first session or it starts within 60 minutes of the group's start time
    if (groupStartTime === null || startTime - groupStartTime < 60) {
      // Add to current group
      currentGroup.push(session);

      // Set group start time if this is the first session in the group
      if (groupStartTime === null) {
        groupStartTime = startTime;
      }
    } else {
      // This session starts 60+ minutes after the group started, so finalize current group
      result.push(...processGroup(currentGroup));

      // Start new group with this session
      currentGroup = [session];
      groupStartTime = startTime;
    }
  }

  // Process final group
  if (currentGroup.length > 0) {
    result.push(...processGroup(currentGroup));
  }

  return result;
};

export { getSessionDataForModal, getSpeakerDataForModal, groupOverlappingSessions };
